import { atom } from 'nanostores';

// Ask the live preview to open an app path ("/products/sunset-tee"). Plugin
// panels and admin extensions call navigatePreview via `panel.navigate('/…')`;
// Preview.tsx listens and points its iframe at the path on the preview's own
// origin (and under /p/<chatId>/ for the static preview).

export interface PreviewNavigation {
  path: string;
  /** Bumps on every request, so asking for the same path twice still navigates. */
  nonce: number;
}

export const $previewNavigation = atom<PreviewNavigation | null>(null);

let nonce = 0;

/** A safe app-relative path ("/x?y#z"), or null. */
export function normalizePreviewPath(path: string): string | null {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || path.includes('\\')) {
    return null;
  }

  try {
    // Resolve against a dummy origin: anything that escapes it is not a path.
    const u = new URL(path, 'https://preview.invalid');

    return u.origin === 'https://preview.invalid' ? `${u.pathname}${u.search}${u.hash}` : null;
  } catch {
    return null;
  }
}

/**
 * The iframe src for `path`, kept on the current preview's origin. A static
 * preview (`/p/<chatId>/…`) keeps its prefix. Null when the current src can't
 * be parsed.
 */
export function previewNavigationUrl(currentSrc: string, path: string, baseOrigin: string): string | null {
  const safe = normalizePreviewPath(path);

  if (!safe) {
    return null;
  }

  try {
    const current = new URL(currentSrc, baseOrigin);
    const prefix = /^\/p\/[^/]+/.exec(current.pathname)?.[0] ?? '';

    return new URL(`${prefix}${safe}`, current.origin).toString();
  } catch {
    return null;
  }
}

/** Request a preview navigation. Returns false for an unsafe path. */
export function navigatePreview(path: string): boolean {
  const safe = normalizePreviewPath(path);

  if (!safe) {
    return false;
  }

  nonce += 1;
  $previewNavigation.set({ path: safe, nonce });

  return true;
}
